import { useEffect, useState } from "react";

const STORAGE_KEY = "pixiweb_consent_v1";

export type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

function saveConsent(state: ConsentState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("pixiweb:consent", { detail: state }));
}

export function CookieConsent() {
  // Start visible so the banner is part of the initial SSR HTML (better LCP).
  // If the user already gave consent, we hide it on the client after hydration.
  const [visible, setVisible] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (readConsent()) setVisible(false);
    const reopen = () => {
      const s = readConsent();
      if (s) {
        setAnalytics(s.analytics);
        setMarketing(s.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener("pixiweb:open-consent", reopen);
    return () => window.removeEventListener("pixiweb:open-consent", reopen);
  }, []);

  if (!visible) return null;

  const accept = (a: boolean, m: boolean) => {
    saveConsent({ necessary: true, analytics: a, marketing: m, ts: Date.now() });
    setVisible(false);
    setShowDetails(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="pw-consent-title"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-mist bg-white p-5 shadow-pixi-lg sm:p-6">
        <div className="flex flex-col gap-4">
          <div>
            <h2 id="pw-consent-title" className="font-display text-lg font-semibold text-navy">
              Wir respektieren Ihre Privatsphäre
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate">
              Wir verwenden technisch notwendige Cookies, damit diese Website funktioniert. Mit Ihrer
              Einwilligung setzen wir zusätzlich <strong>Google Analytics 4</strong> ein, um die
              Nutzung anonymisiert zu messen (Cookies <code>_ga</code>, <code>_ga_*</code>;
              IP-Anonymisierung aktiv; Datenübertragung in die USA möglich; Rechtsgrundlage Art. 6
              Abs. 1 lit. a DSGVO / § 25 Abs. 1 TTDSG). Sie können Ihre Auswahl jederzeit über
              „Cookie-Einstellungen“ im Footer widerrufen. Details in unserer{" "}
              <a href="/datenschutz" className="font-semibold text-navy underline underline-offset-2">
                Datenschutzerklärung
              </a>
              .
            </p>
          </div>

          {showDetails && (
            <div className="space-y-3 rounded-xl border border-mist bg-ice p-4 text-sm">
              <Row
                title="Notwendig"
                desc="Erforderlich für den Betrieb der Website (z. B. Speicherung Ihrer Cookie-Auswahl). Immer aktiv."
                checked
                disabled
              />
              <Row
                title="Analyse (Google Analytics 4)"
                desc="Misst Seitenaufrufe und Nutzung anonymisiert mit Google Analytics 4 (Cookies _ga, _ga_*). Anbieter: Google Ireland Ltd., Datenübertragung in die USA möglich."
                checked={analytics}
                onChange={setAnalytics}
              />
              <Row
                title="Marketing"
                desc="Wird für personalisierte Inhalte und Anzeigen verwendet. Aktuell nicht im Einsatz."
                checked={marketing}
                onChange={setMarketing}
              />
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={() => accept(false, false)}
              className="rounded-xl border border-mist bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-ice"
            >
              Nur notwendige
            </button>
            {!showDetails ? (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="rounded-xl border border-mist bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-ice"
              >
                Einstellungen
              </button>
            ) : (
              <button
                type="button"
                onClick={() => accept(analytics, marketing)}
                className="rounded-xl border border-mist bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-ice"
              >
                Auswahl speichern
              </button>
            )}
            <button
              type="button"
              onClick={() => accept(true, true)}
              className="rounded-xl bg-pixi-gradient px-5 py-2.5 text-sm font-semibold text-white shadow-pixi hover:shadow-pixi-lg"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4">
      <div>
        <p className="font-semibold text-navy">{title}</p>
        <p className="mt-0.5 text-xs text-slate">{desc}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 h-4 w-4 accent-electric disabled:opacity-60"
      />
    </label>
  );
}

export function openConsentSettings() {
  window.dispatchEvent(new Event("pixiweb:open-consent"));
}
